import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { FeedbackTableValues } from '../types/feedbacktable';

const getData = async ({
  cohortId,
  feedbackStatus,
  limit,
  offset,
  latestFeedbackId,
}: {
  cohortId: number;
  feedbackStatus: string;
  limit: number;
  offset: number;
  latestFeedbackId?: string;
  // eslint-disable-next-line consistent-return
}) => {
  const url = endpoints.performanceReview.continuousFeedback.feedbackTable;

  try {
    const res = await sendRequest({
      url,
      method: 'POST',
      credentials: 'include',
      body: {
        cohortId,
        dateRange: null,
        groupByKeys: null,
        orderBy: null,
      },
      queryParams: {
        limit,
        offset,
        feedback_status: feedbackStatus,
        latestFeedbackId,
      },
    });

    return res?.entity;
  } catch (error) {
    console.error('Error in fetching feedback table data', error);
  }
};

export const useGetFeedbackTable = ({
  cohortId,
  feedbackStatus,
  limit,
  offset,
}: {
  cohortId: number;
  feedbackStatus: string;
  limit: number;
  offset: number;
  // eslint-disable-next-line arrow-body-style
}) => {
  return useQuery<{
    columns: Array<Record<string, unknown>>;
    values: Array<FeedbackTableValues>;
  }>({
    queryKey: [REACT_QUERY_KEYS.feedbackTable, cohortId, feedbackStatus, limit, offset],
    queryFn: () => getData({ cohortId, feedbackStatus, limit, offset }),
    enabled: !!cohortId,
  });
};

export const useGetInfiniteFeedbackTable = ({
  cohortId,
  feedbackStatus,
  limit,
}: {
  cohortId: number;
  feedbackStatus: string;
  limit: number;
}) => {
  const queryData = useInfiniteQuery({
    queryKey: [REACT_QUERY_KEYS.feedbackTable, cohortId, feedbackStatus],
    queryFn: ({ pageParam = null }) =>
      getData({ cohortId, feedbackStatus, limit, offset: 0, latestFeedbackId: pageParam }),
    getNextPageParam: (lastPage) => {
      if (lastPage?.values?.length === limit) {
        // return the uuid of the last feedback in the current page
        const feedbacksLength = lastPage?.values?.length;
        return lastPage?.values[feedbacksLength - 1]?.comment?.uuid;
        // return pages?.length; // The next page offset
        // eslint-disable-next-line no-else-return
      } else {
        return undefined; // No more pages
      }
    },
    enabled: !!cohortId,
    refetchOnWindowFocus: false,
  });

  return queryData;
};
